import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core'
import { MessageService } from 'primeng/api'

import { AuthService } from '@data/services/modules/auth/auth.service'

import { WorklogEditorComponent } from '../worklog-editor/worklog-editor.component'

import { TransitionOptions, WorklogService, worklogErrorMessage } from '../../data/services/worklog.service'
import { ActivityEntry, AreaPoints, WorkItem, WorkItemArea, WorkItemAttachment, WorkItemComment, WorkItemHistory, WorkItemStatus, WorkUserRef } from '../../data/interfaces/worklog.interface'
import {
  AREA_META,
  AREA_ORDER,
  EFFORT_POINTS,
  ORIGIN_META,
  PRIORITY_META,
  SEVERITY_META,
  STATUS_META,
  statusMetaOf,
  TYPE_META,
  WORK_ITEM_TRANSITIONS,
} from '../../data/constants/worklog.constants'
import { isValidSplit, needsSplit, seedSplit, splitLabel } from '../../data/utils/area-split.util'
import { fileIconOf, fileSizeLabel } from '../../data/utils/attachment.util'
import { needsClassification } from '../../data/utils/classification.util'

//* Ventana para plegar eventos de campo del mismo actor en una sola línea. Dos minutos: una
//* clasificación (puntos + áreas + épica) se escribe en el mismo request y cae en el mismo
//* segundo, pero una corrección a mano tarda un poco más y también es la misma acción.
const FOLD_WINDOW_MS = 2 * 60 * 1000

//* Campos de texto libre. En la Actividad se anuncia QUE cambiaron, sin volcar el antes y el
//* después: una descripción con una imagen pegada mete su URL completa en la línea y sepulta el
//* resto del stream. Quien quiera el detalle lo tiene arriba, en la descripción misma.
const LONG_TEXT_FIELDS = ['description', 'acceptanceCriteria', 'stepsToReproduce', 'expectedResult']

/**
 * Modal de detalle de un item (estilo tarjeta ClickUp): quién lo solicitó y cuándo,
 * descripción y criterios, etiquetas, comentarios e historial de movimientos.
 *
 * Lo único editable aquí es lo que decide el equipo de desarrollo: el estado (desde el badge
 * del encabezado) y la fecha de entrega. El esfuerzo lo registra el agente al commitear —o se
 * pide al mandar a Deploy si falta—, el responsable se asigna solo y el origen lo deriva el
 * backend de quién dio de alta la tarea.
 */
@Component({
  selector: 'app-worklog-item-detail',
  templateUrl: './worklog-item-detail.component.html',
  styleUrls: ['./worklog-item-detail.component.scss'],
})
export class WorklogItemDetailComponent implements OnChanges, OnDestroy {
  @Input() itemId: string | null = null
  @Input() visible = false
  @Input() isDev = false
  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() changed = new EventEmitter<void>()

  item: WorkItem | null = null
  comments: WorkItemComment[] = []
  history: WorkItemHistory[] = []
  //* TODOS los adjuntos, retirados incluidos: los retirados siguen haciendo falta para resolver
  //* una imagen que quedó pegada dentro de un texto. La galería usa `visibleAttachments`.
  attachments: WorkItemAttachment[] = []
  loading = false

  /**
   * Historial y comentarios mezclados en UNA línea de tiempo. Antes eran dos secciones apiladas:
   * para saber si el comentario llegó antes o después del rebote de QA había que comparar horas
   * saltando de una a la otra, que es exactamente lo que uno va a leer ahí.
   */
  activity: ActivityEntry[] = []
  /** Grupos de eventos de campo desplegados por el usuario ("Mostrar más" de cada línea). */
  expanded = new Set<string>()

  /**
   * URL del blob → object URL descargado por la API. El contenedor del bucket no es público (409),
   * así que ninguna imagen se puede pintar apuntando directo al blob: hay que traerla con el token
   * delante y traducirla. Se revoca en `ngOnDestroy` — sin eso cada apertura del detalle filtra
   * tantos blobs en memoria como adjuntos tenga la tarea.
   */
  attachmentUrls: Record<string, string> = {}
  /**
   * Las URLs que SÍ son adjuntos de esta tarea. Se calcula al cargar y no en un getter: un getter
   * devolvería un arreglo nuevo en cada ciclo de detección de cambios y obligaría a los cuatro
   * `app-worklog-rich-text` del detalle a reparsear su texto entero cada vez.
   */
  attachmentRefs: string[] = []

  /**
   * Qué pestaña se ve en la columna derecha.
   *
   * Arranca SIEMPRE en la Bitácora: es el historial del trabajo y se consulta cada vez que se abre
   * una tarea, mientras que los pasos del reporte se miran una sola vez, al ir a reproducir el
   * fallo. Cuando la traza estaba arriba y desplegada, enterraba a la Bitácora con sus 60 filas.
   */
  tab: 'activity' | 'trace' = 'activity'

  /** Hay pasos que enseñar: solo entonces aparecen las pestañas. */
  hasTrace = false

  /** Cuántos pasos, para el contador de la pestaña. */
  traceCount = 0

  /**
   * Imagen abierta en grande. La visibilidad va en su propio booleano porque `p-dialog` escribe
   * `false` en el binding al cerrarse, y eso no cabe en un `string | null`.
   */
  lightboxUrl: string | null = null
  lightboxVisible = false
  uploading = false

  //* El editor de la caja de comentario. Hace falta la referencia para cambiarle el token de una
  //* imagen por su URL definitiva en cuanto termina de subir, sin tocar lo que se está escribiendo.
  @ViewChild('commentEditor') commentEditor?: WorklogEditorComponent

  newComment = ''
  //* Imágenes pegadas en el comentario que todavía están subiendo. Mientras haya alguna no se puede
  //* enviar: el texto llevaría un token `(adjunto:N)` que no significa nada fuera de esta pantalla.
  commentUploads = 0
  //* Destino pendiente mientras se piden los puntos para pasar a Deploy.
  moveTarget: WorkItemStatus | null = null
  //* Ligado al dropdown de asignación para poder reflejar el asignado actual y limpiarlo
  //* tras recargar. Sin binding, PrimeNG conserva un valor huérfano entre aperturas.
  //* Fecha de entrega en el formato que espera `p-calendar` (el item la trae como ISO string).
  //! Se calcula UNA vez al cargar, no en un getter: un getter devolvería un Date nuevo en cada
  //! ciclo de detección de cambios, `ngModel` nunca vería el mismo objeto y el ciclo no cerraría
  //! nunca — la pestaña se congela igual que con un bucle de router.
  dueDate: Date | null = null

  //* Puntuación al entregar: mandar a Deploy exige esfuerzo puntuado (el backend también lo
  //* valida). Si la tarea no lo trae, el movimiento se detiene aquí y se piden los puntos.
  askingPoints = false
  chosenPoints: number | null = null
  //* Áreas elegidas en el mismo paso que el esfuerzo: Deploy exige la tarea ya clasificada.
  chosenAreas: string[] = []
  //* Con dos o más áreas, cuántos de los puntos fueron de cada una (F-156). Se siembra en partes
  //* iguales al cambiar puntos o áreas, y se corrige a mano.
  chosenAreaPoints: AreaPoints | null = null

  //* Reparto de la tarea en la ficha: cómo se lee y si lo decidió alguien o es estimado.
  splitView: { label: string; estimated: boolean } | null = null
  //* Edición del reparto desde la ficha (isDev). También se abre al cambiar los puntos de una
  //* tarea con varias áreas: puntos y reparto se guardan juntos o el reparto dejaría de cuadrar.
  editingSplit = false
  savingSplit = false
  editPoints: number | null = null
  editAreaPoints: AreaPoints | null = null
  //* Valor del dropdown de puntos de la ficha. Propio y no `item.points`: en una tarea con varias
  //* áreas elegir puntos abre el reparto sin guardar, y al cancelar el dropdown debe volver a lo
  //* guardado en vez de quedarse mostrando un valor que no existe.
  pointsControl: number | null = null
  //* Cancelar exige motivo: queda en el historial como la razón del descarte.
  askingReason = false
  cancelReason = ''
  //* Borrar es irreversible y no deja fila en ninguna parte, así que se pregunta aparte de cancelar
  //* y con otras palabras: son dos cosas distintas y confundirlas cuesta el registro de la tarea.
  askingDelete = false
  deleting = false
  readonly EFFORT_POINTS = EFFORT_POINTS
  readonly AREA_ORDER = AREA_ORDER

  //* Refinamiento en vivo (solo isDev): asignación, esfuerzo y origen del KPI. El backend
  //* valida lo mismo; la UI solo se lo ofrece a quien puede ejecutarlo.
  devs: WorkUserRef[] = []
  devOptions: { label: string; value: string }[] = []
  assigneeId: string | null = null
  readonly pointOptions = EFFORT_POINTS.map((p) => ({ label: `${p.value} pts — ${p.hint}`, value: p.value }))

  readonly TYPE_META = TYPE_META
  readonly AREA_META = AREA_META
  readonly STATUS_META = STATUS_META
  readonly PRIORITY_META = PRIORITY_META
  readonly SEVERITY_META = SEVERITY_META
  readonly ORIGIN_META = ORIGIN_META

  constructor(private worklog: WorklogService, private messageService: MessageService, private auth: AuthService) {}

  /**
   * La traza avisa cuántos pasos trajo. Es lo que decide si la pestaña existe.
   *
   * Sube por evento y no se consulta desde aquí porque el dato lo pide el hijo: duplicar la llamada
   * para saber si mostrar su propia pestaña sería pedir dos veces lo mismo.
   */
  onTraceLoaded(count: number): void {
    this.traceCount = count
    this.hasTrace = count > 0
    //* Si la traza no llegó, no puede quedarse una pestaña seleccionada que ya no existe.
    if (!this.hasTrace) this.tab = 'activity'
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['itemId'] || changes['visible']) && this.visible && this.itemId) {
      this.load(this.itemId)
    }
  }

  private load(id: string): void {
    this.loading = true
    this.item = null
    this.moveTarget = null
    this.askingPoints = false
    this.chosenPoints = null
    this.chosenAreas = []
    this.chosenAreaPoints = null
    this.editingSplit = false
    this.savingSplit = false
    this.splitView = null
    this.askingReason = false
    this.cancelReason = ''
    this.askingDelete = false
    this.activity = []
    this.attachmentRefs = []
    //* Cambiar de tarea vuelve a la Bitácora: la pestaña de pasos es de la tarea anterior.
    this.tab = 'activity'
    this.hasTrace = false
    this.traceCount = 0
    this.expanded.clear()
    this.lightboxUrl = null
    this.lightboxVisible = false
    this.worklog.getItem(id).subscribe((it) => {
      this.item = it
      this.pointsControl = it?.points ?? null
      this.splitView = this.buildSplitView(it)
      this.dueDate = it?.dueDate ? new Date(it.dueDate) : null
      this.assigneeId = it?.assignee && typeof it.assignee !== 'string' ? it.assignee._id : ((it?.assignee as string) ?? null)
      this.loading = false
      this.buildActivity()
    })
    this.worklog.getComments(id).subscribe((c) => {
      this.comments = c
      this.buildActivity()
    })
    this.worklog.getHistory(id).subscribe((h) => {
      this.history = h
      this.buildActivity()
    })
    this.refreshAttachments(id)
    //* El catálogo de devs se pide una vez por apertura y solo a quien puede reasignar.
    if (this.isDev && !this.devs.length) {
      this.worklog.getDevs().subscribe((devs) => {
        this.devs = devs
        this.devOptions = devs.map((d) => ({ label: this.userName(d), value: d._id }))
      })
    }
  }

  /** Reasigna el ejecutor (isDev). El backend registra el cambio en el event-log. */
  setAssignee(assignee: string | null): void {
    if (!this.item || !assignee) return
    this.worklog.assign(this.item._id, assignee).subscribe({
      next: (updated) => {
        if (!updated) return
        this.toast('success', 'Reasignada', 'Se cambió el responsable de la tarea.')
        this.load(this.item!._id)
        this.changed.emit()
      },
      error: (err) => this.toast('error', 'No se pudo reasignar', worklogErrorMessage(err)),
    })
  }

  /**
   * Corrige el esfuerzo puntuado (isDev) — p. ej. tras re-calibrar al cierre. En una tarea con varias
   * áreas abre el reparto: cambiar los puntos sin él dejaría un reparto que ya no suma.
   */
  setPoints(points: number | null): void {
    if (points == null || !this.item) return
    this.pointsControl = points
    if (needsSplit(this.item.areas)) {
      this.openSplitEditor(points)
      return
    }
    this.saveRefinement({ points }, 'Se actualizó el esfuerzo.')
  }

  /** Abre el editor del reparto en la ficha, con los puntos vigentes o los que se van a fijar. */
  openSplitEditor(points: number | null = this.item?.points ?? null): void {
    if (!this.item || points == null) return
    this.editPoints = points
    this.editAreaPoints = seedSplit(points, this.item.areas, this.item.areaPoints)
    this.editingSplit = true
  }

  get canSaveSplit(): boolean {
    return !!this.item && isValidSplit(this.editPoints, this.item.areas, this.editAreaPoints)
  }

  /** El editor sigue abierto hasta que el backend responde: si falla, lo capturado no se pierde. */
  saveSplit(): void {
    if (!this.item || !this.canSaveSplit || this.savingSplit) return
    this.savingSplit = true
    this.saveRefinement({ points: this.editPoints, areaPoints: this.editAreaPoints }, 'Se guardó el reparto de los puntos.', () => (this.savingSplit = false))
  }

  cancelSplitEdit(): void {
    this.editingSplit = false
    this.editAreaPoints = null
    this.pointsControl = this.item?.points ?? null
  }

  /** Cómo se lee el reparto en la ficha. `null` cuando no aplica (sin puntos o con una sola área). */
  private buildSplitView(it: WorkItem | null): { label: string; estimated: boolean } | null {
    if (!it || it.points == null || !needsSplit(it.areas)) return null
    const isValid = isValidSplit(it.points, it.areas, it.areaPoints)
    return { label: splitLabel(isValid ? it.areaPoints : seedSplit(it.points, it.areas, null)), estimated: !isValid }
  }

  /** Corrige el origen del KPI de aportes (isDev humano; el backend lo veta al agente). */
  toggleOrigin(): void {
    if (!this.item) return
    const origin = this.item.origin === 'proposed' ? 'requested' : 'proposed'
    this.saveRefinement({ origin }, origin === 'proposed' ? 'Marcada como aporte del equipo (Extra).' : 'Marcada como solicitada.')
  }

  get allowedMoves(): WorkItemStatus[] {
    return this.item ? WORK_ITEM_TRANSITIONS[this.item.status] ?? [] : []
  }

  /**
   * ¿El badge de estado funciona como control para mover? Solo para un dev y cuando el estado
   * actual tiene destinos válidos (los terminales, Cerrada y Cancelada, no tienen).
   */
  get canChangeStatus(): boolean {
    return this.isDev && this.allowedMoves.length > 0
  }

  /** Referencia de calibración del valor elegido, para que puntuar no sea a ojo. */
  get pointsHint(): string {
    return EFFORT_POINTS.find((p) => p.value === this.chosenPoints)?.hint ?? ''
  }

  /**
   * Compromiso en riesgo (ADR-0040 · D5): el equipo se comprometió a una fecha posterior a la
   * fecha fija que pidió quien reporta. Se pinta en rojo — es información, no un bloqueo.
   */
  get dueAtRisk(): boolean {
    if (!this.item?.dueDate || !this.item?.requestedDueDate) return false
    return new Date(this.item.dueDate).getTime() > new Date(this.item.requestedDueDate).getTime()
  }

  /**
   * ¿El movimiento pedido obliga a clasificar la tarea antes de continuar? Deploy exige esfuerzo
   * puntuado y áreas: sin lo primero no cuenta para la velocidad, sin lo segundo no se puede
   * atribuir a ningún proyecto en las métricas. El backend valida lo mismo.
   */
  private needsClassificationFor(to: WorkItemStatus): boolean {
    return needsClassification(this.item, to)
  }

  userName(u?: WorkUserRef | string | null): string {
    if (!u) return '—'
    if (typeof u === 'string') return '—'
    return `${u.names ?? ''} ${u.lastNames ?? ''}`.trim() || u.email || '—'
  }

  initials(u?: WorkUserRef | string | null): string {
    if (!u || typeof u === 'string') return '?'
    return `${(u.names ?? '?')[0] ?? ''}${(u.lastNames ?? '')[0] ?? ''}`.toUpperCase()
  }

  areaMeta(a: string) {
    return (AREA_META as any)[a]
  }

  //* El historial guarda los nombres de su época: una fila vieja puede decir «Capacitación».
  statusLabel(s?: string | null): string {
    if (!s) return ''
    return statusMetaOf(s)?.label ?? s
  }

  statusColor(s?: string | null): string {
    if (!s) return '#64748b'
    return statusMetaOf(s)?.color ?? '#64748b'
  }

  // ─────────── Historial: tres clases de evento, tres lecturas ───────────

  //* El backend guarda los valores como los conoce (`proposed`, `requested`, el nombre del
  //* sprint). El historial los mostraba en crudo —«origin: proposed → requested»—, que es la
  //* representación interna, no lo que el usuario eligió en la pantalla.
  private readonly FIELD_LABELS: Record<string, string> = {
    origin: 'Origen',
    points: 'Esfuerzo',
    estimatedPoints: 'Estimado',
    areas: 'Áreas',
    areaPoints: 'Reparto',
    assignee: 'Asignado',
    dueDate: 'Entrega',
    epic: 'Épica',
    sprint: 'Sprint',
    flowStartedAt: 'Inicio real',
    deliveredAt: 'Entrega real',
    //* Sin estas, un cambio de descripción salía rotulado `description:` — el nombre crudo del
    //* campo, en inglés, en una pantalla en español.
    title: 'Título',
    description: 'Descripción',
    acceptanceCriteria: 'Criterios',
    stepsToReproduce: 'Pasos',
    expectedResult: 'Resultado esperado',
    priority: 'Prioridad',
    severity: 'Severidad',
  }

  /**
   * Cómo salió la estimación frente al costo real. Solo cuando existen las dos: comparar contra
   * un estimado ausente diría "se pasó 5 puntos" de algo que nadie estimó.
   */
  get estimateDrift(): { label: string; level: string } | null {
    const est = this.item?.estimatedPoints
    const real = this.item?.points
    if (est == null || real == null) return null
    if (est === real) return { label: `estimado ${est} · clavado`, level: 'ok' }
    const dif = real - est
    return {
      label: `estimado ${est} · ${dif > 0 ? '+' : ''}${dif}`,
      level: Math.abs(dif) <= 2 ? 'ok' : 'off',
    }
  }

  /** El fin del sprint como compromiso por defecto: solo si la tarea está en uno y no tiene fecha. */
  get canUseSprintEnd(): boolean {
    const sp: any = this.item?.sprint
    return this.isDev && !this.item?.dueDate && !!sp && typeof sp === 'object' && !!sp.endDate
  }

  useSprintEnd(): void {
    const sp: any = this.item?.sprint
    if (!sp?.endDate) return
    this.setDueDate(new Date(sp.endDate))
  }

  /**
   * Qué precede a los dos valores del evento. Un arrastre ya nombra el sprint en sus valores, así
   * que anteponerle «Sprint:» lo dejaba en «Sprint: Sprint 14 → Sprint 15».
   */
  eventPrefix(h: WorkItemHistory): string {
    if (h.kind === 'carryOver') return 'Arrastre'
    if (h.kind === 'field' && h.field) return this.FIELD_LABELS[h.field] ?? h.field
    return ''
  }

  /** Qué clase de evento es, para el tooltip del punto. */
  eventKindLabel(h: WorkItemHistory): string {
    if (h.kind === 'carryOver') return 'Arrastrada al siguiente sprint'
    if (h.kind === 'field') return 'Cambio de dato'
    return 'Movimiento en el tablero'
  }

  /**
   * Valor legible de un extremo del evento. Una transición nombra estados; un cambio de campo
   * nombra el valor del campo —y `origin` tiene sus propias etiquetas—; el arrastre de sprint ya
   * viene con el nombre puesto.
   */
  eventValue(h: WorkItemHistory, value?: string | null): string {
    if (!value) return '—'
    if (h.kind === 'status') return this.statusLabel(value)
    if (h.field === 'origin') return (ORIGIN_META as any)[value]?.label ?? value
    if (h.field === 'areaPoints' || h.field === 'areas') return splitLabel(value)
    return value
  }

  /** El arrastre se distingue en ámbar: no es avance del trabajo, es que no cupo en el sprint. */
  eventColor(h: WorkItemHistory): string {
    if (h.kind === 'carryOver') return '#f59e0b'
    if (h.kind === 'field') return '#64748b'
    return this.statusColor(h.to)
  }

  close(): void {
    this.visible = false
    this.visibleChange.emit(false)
  }

  /** Mueve al estado elegido en el badge. */
  moveTo(to: WorkItemStatus): void {
    if (!this.item) return
    this.moveTarget = to
    //* Cancelar pide el motivo antes de nada; el envío ocurre en confirmCancel().
    if (to === 'Cancelada') {
      this.cancelReason = ''
      this.askingReason = true
      return
    }
    //* Pide los puntos antes de mandar a Deploy; el envío real ocurre en confirmPoints().
    if (this.needsClassificationFor(to)) {
      this.chosenPoints = this.item.points ?? null
      this.chosenAreas = [...(this.item.areas ?? [])]
      this.chosenAreaPoints = seedSplit(this.chosenPoints, this.chosenAreas as WorkItemArea[], this.item.areaPoints)
      this.askingPoints = true
      return
    }
    this.sendMove(to)
  }

  /** ¿Ya está todo lo que To Deploy exige para dejar avanzar? */
  get canConfirmPoints(): boolean {
    return this.chosenPoints != null && this.chosenAreas.length > 0 && isValidSplit(this.chosenPoints, this.chosenAreas, this.chosenAreaPoints)
  }

  pickPoints(points: number): void {
    this.chosenPoints = points
    this.reseedSplit()
  }

  toggleArea(area: string): void {
    this.chosenAreas = this.chosenAreas.includes(area) ? this.chosenAreas.filter((a) => a !== area) : [...this.chosenAreas, area]
    this.reseedSplit()
  }

  /** Conserva el reparto si sigue cuadrando con los puntos y áreas nuevos; si no, partes iguales. */
  private reseedSplit(): void {
    this.chosenAreaPoints = seedSplit(this.chosenPoints, this.chosenAreas as WorkItemArea[], this.chosenAreaPoints)
  }

  /** Confirma esfuerzo, áreas y reparto, y ejecuta el movimiento pendiente en el mismo request. */
  confirmPoints(): void {
    if (!this.moveTarget || !this.canConfirmPoints) return
    this.sendMove(this.moveTarget, {
      points: this.chosenPoints!,
      areas: this.chosenAreas,
      ...(needsSplit(this.chosenAreas) ? { areaPoints: this.chosenAreaPoints } : {}),
    })
  }

  cancelPoints(): void {
    this.askingPoints = false
    this.chosenPoints = null
    this.chosenAreas = []
    this.chosenAreaPoints = null
  }

  /** Confirma la cancelación con su motivo, que queda registrado en el historial. */
  confirmCancel(): void {
    if (!this.moveTarget || !this.cancelReason.trim()) return
    this.sendMove('Cancelada', { note: this.cancelReason.trim() })
  }

  dismissCancel(): void {
    this.askingReason = false
    this.cancelReason = ''
    this.moveTarget = null
  }

  /**
   * ¿Se le ofrece borrar? Solo mientras la tarea siga en Backlog —ahí no hay trabajo detrás que
   * perder— y solo a quien la registró o a un Admin. El backend valida lo mismo; esto únicamente
   * evita mostrar un botón que va a dar 403.
   */
  get canDelete(): boolean {
    if (!this.item || this.item.status !== 'Backlog') return false
    const user = this.auth.getUser?.user
    if (!user) return false
    const creador = this.item.createdBy
    const esSuya = !!creador && typeof creador !== 'string' && String(creador._id) === String(user.id)
    return esSuya || user.permissions?.name === 'Admin'
  }

  askDelete(): void {
    this.askingDelete = true
  }

  dismissDelete(): void {
    this.askingDelete = false
  }

  confirmDelete(): void {
    if (!this.item || this.deleting) return
    this.deleting = true
    const code = this.item.code ?? 'La tarea'
    this.worklog.deleteItem(this.item._id).subscribe({
      next: () => {
        this.deleting = false
        this.askingDelete = false
        this.toast('success', 'Tarea borrada', `${code} y su historial se eliminaron.`)
        this.changed.emit()
        this.close()
      },
      error: (err) => {
        this.deleting = false
        this.toast('error', 'No se pudo borrar', worklogErrorMessage(err, 'No se pudo borrar la tarea.'))
      },
    })
  }

  private sendMove(to: WorkItemStatus, opts: TransitionOptions = {}): void {
    if (!this.item) return
    this.worklog.transition(this.item._id, to, opts).subscribe({
      next: (updated) => {
        if (!updated) return
        this.askingPoints = false
        this.chosenPoints = null
        this.chosenAreas = []
        this.chosenAreaPoints = null
        this.askingReason = false
        this.cancelReason = ''
        this.moveTarget = null
        this.toast('success', 'Tarea movida', `Ahora está en ${this.statusLabel(to)}.`)
        this.load(this.item!._id)
        this.changed.emit()
      },
      error: (err) => this.toast('error', 'No se pudo mover', worklogErrorMessage(err, 'No se pudo mover la tarea.')),
    })
  }

  /**
   * Guarda un campo de refinamiento (esfuerzo, fecha de entrega, origen). Solo un `isDev`
   * llega aquí desde la UI y el backend descarta estos campos si el actor no lo es.
   */
  saveRefinement(patch: Partial<WorkItem>, label: string, onFailure?: () => void): void {
    if (!this.item) return
    this.worklog.updateItem(this.item._id, patch).subscribe({
      next: (updated) => {
        if (!updated) return onFailure?.()
        this.toast('success', 'Actualizado', label)
        this.load(this.item!._id)
        this.changed.emit()
      },
      error: (err) => {
        onFailure?.()
        this.toast('error', 'No se pudo actualizar', worklogErrorMessage(err))
      },
    })
  }

  setDueDate(d: Date | null): void {
    this.saveRefinement({ dueDate: d ? new Date(d).toISOString() : null }, d ? 'Se guardó la fecha de entrega.' : 'Se quitó la fecha de entrega.')
  }


  /** Hay algo que decir y ninguna imagen a medio subir. */
  get canSendComment(): boolean {
    return !!this.newComment.trim() && !this.commentUploads
  }

  sendComment(): void {
    if (!this.item || !this.canSendComment) return
    this.worklog.addComment(this.item._id, this.newComment.trim()).subscribe({
      next: (c) => {
        if (!c) return
        this.newComment = ''
        this.worklog.getComments(this.item!._id).subscribe((list) => {
          this.comments = list
          this.buildActivity()
        })
      },
      error: (err) => this.toast('error', 'No se pudo comentar', worklogErrorMessage(err)),
    })
  }

  // ─────────── Actividad unificada ───────────

  /**
   * Arma el stream: alta + movimientos + comentarios en una sola línea de tiempo ascendente.
   *
   * Se recalcula cada vez que llega una de las tres fuentes porque las tres peticiones vuelven por
   * separado; construirlo solo al final obligaría a un `forkJoin` que retrasaría la primera pintada
   * hasta la más lenta.
   *
   * Los eventos de campo consecutivos del mismo actor se pliegan en una línea expandible: puntuar
   * una tarea escribe cuatro o cinco filas seguidas (puntos, áreas, épica, entrega) que de otro
   * modo entierran el comentario de al lado.
   */
  private buildActivity(): void {
    const entries: ActivityEntry[] = []

    if (this.item?.createdAt) {
      entries.push({ type: 'created', at: this.item.createdAt, who: this.item.createdBy ?? null })
    }
    for (const h of this.history) entries.push({ type: 'event', at: h.occurredAt, who: h.actor ?? null, event: h })
    for (const c of this.comments) entries.push({ type: 'comment', at: c.createdAt ?? '', who: c.author, comment: c })

    entries.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
    this.activity = this.foldFieldEvents(entries)
  }

  /** Pliega rachas de eventos `field` del mismo actor separadas por menos de `FOLD_WINDOW_MS`. */
  private foldFieldEvents(entries: ActivityEntry[]): ActivityEntry[] {
    const out: ActivityEntry[] = []
    for (const entry of entries) {
      const prev = out[out.length - 1]
      const foldable = entry.type === 'event' && entry.event?.kind === 'field'
      const prevFoldable = prev?.type === 'event' && prev.event?.kind === 'field'
      const sameActor = prevFoldable && this.userName(prev.who) === this.userName(entry.who)
      const close = prevFoldable && new Date(entry.at).getTime() - new Date(prev.at).getTime() < FOLD_WINDOW_MS

      if (foldable && sameActor && close) {
        prev.grouped = [...(prev.grouped ?? [prev.event!]), entry.event!]
        //* El instante del grupo avanza al último evento: es la hora que la gente busca ("¿cuándo
        //* se puntuó?"), no la del primer campo de la racha.
        prev.at = entry.at
        continue
      }
      out.push({ ...entry })
    }
    return out
  }

  /** Identidad estable del grupo para el Set de desplegados (el índice cambia al recargar). */
  groupKey(entry: ActivityEntry): string {
    return entry.event?._id ?? entry.comment?._id ?? entry.at
  }

  toggleGroup(entry: ActivityEntry): void {
    const key = this.groupKey(entry)
    this.expanded.has(key) ? this.expanded.delete(key) : this.expanded.add(key)
  }

  isExpanded(entry: ActivityEntry): boolean {
    return this.expanded.has(this.groupKey(entry))
  }

  /** El adjunto ya populado de un evento `attachment`, o null si viene solo como id. */
  eventAttachment(h?: WorkItemHistory | null): WorkItemAttachment | null {
    const a = h?.attachment
    return a && typeof a !== 'string' ? a : null
  }

  // ─────────── Adjuntos ───────────

  isImage(a: WorkItemAttachment): boolean {
    return (a.mimeType ?? '').startsWith('image/')
  }

  fileSize(bytes: number): string {
    return fileSizeLabel(bytes)
  }

  fileIcon(a: WorkItemAttachment): string {
    return fileIconOf(a.mimeType, a.filename)
  }

  /** Archivos elegidos desde el input o soltados en la zona: se suben como adjuntos de la tarea. */
  onFilesChosen(files: FileList | null): void {
    if (!this.item || !files?.length) return
    this.uploadFiles(Array.from(files))
  }

  onDrop(event: DragEvent): void {
    event.preventDefault()
    this.onFilesChosen(event.dataTransfer?.files ?? null)
  }

  //* Sin este `preventDefault` el navegador ABRE el archivo soltado y se pierde la pantalla con
  //* todo lo que se estaba capturando.
  onDragOver(event: DragEvent): void {
    event.preventDefault()
  }

  /**
   * Imagen pegada en la caja de comentario. El editor ya la dejó visible con un token; aquí se sube
   * en el acto —la tarea existe, hay id al que colgarla— y al volver se le cambia el token por la
   * URL definitiva. Lo que se envía es entonces el mismo markdown de siempre; lo que cambió es que
   * quien escribe ve su captura en vez de la URL del blob.
   */
  onCommentImage({ token, file }: { token: string; file: File }): void {
    if (!this.item) return
    const id = this.item._id
    this.commentUploads++
    this.worklog.uploadAttachment(id, file, { inline: true }).subscribe({
      next: (att) => {
        this.commentUploads--
        //* Si el backend no devolvió URL la imagen no existe: se quita del texto en vez de dejar un
        //* token que se enviaría tal cual y quedaría como un hueco permanente en el comentario.
        if (att?.url) this.commentEditor?.resolveToken(token, att.url)
        else this.commentEditor?.removeImage(token)
        this.refreshAttachments(id)
      },
      error: (err) => {
        this.commentUploads--
        this.commentEditor?.removeImage(token)
        this.toast('error', 'No se pudo subir', worklogErrorMessage(err, 'La imagen no se pudo adjuntar.'))
      },
    })
  }

  /** Archivos del dropzone: cuelgan de la tarea y dejan su evento en la Actividad. */
  private uploadFiles(files: File[]): void {
    if (!this.item) return
    const id = this.item._id
    this.uploading = true
    let pending = files.length
    for (const file of files) {
      this.worklog.uploadAttachment(id, file, { inline: false }).subscribe({
        next: () => {
          if (--pending === 0) this.finishUpload(id)
        },
        error: (err) => {
          this.toast('error', 'No se pudo subir', worklogErrorMessage(err, 'El archivo no se pudo adjuntar.'))
          if (--pending === 0) this.finishUpload(id)
        },
      })
    }
  }

  /** Vuelve a pedir la galería y baja los binarios que falten. */
  private refreshAttachments(id: string): void {
    this.worklog.getAttachments(id).subscribe((a) => {
      this.attachments = a
      this.attachmentRefs = a.map((att) => att.url)
      this.resolveAttachmentUrls(id, a)
    })
  }

  private finishUpload(id: string): void {
    this.uploading = false
    this.refreshAttachments(id)
    //* Un adjunto suelto escribe su evento en el historial y por eso hay que recargarlo; uno pegado
    //* en un texto no lo hace (lo cuenta el texto que lo contiene) y sigue otro camino.
    this.worklog.getHistory(id).subscribe((h) => {
      this.history = h
      this.buildActivity()
    })
  }

  removeAttachment(a: WorkItemAttachment): void {
    if (!this.item) return
    const id = this.item._id
    this.worklog.removeAttachment(id, a._id).subscribe((ok) => {
      if (!ok) {
        this.toast('error', 'No se pudo retirar', 'Solo quien subió el archivo o el equipo de TI puede retirarlo.')
        return
      }
      this.refreshAttachments(id)
    })
  }

  /**
   * Descarga cada adjunto por la API y guarda su object URL. Se hace por adjunto y no en bloque
   * para que la galería se vaya pintando conforme llegan, en vez de quedarse en blanco hasta el
   * último. Los ya resueltos no se vuelven a pedir: el detalle recarga los adjuntos en cada subida
   * y sin esta guarda cada recarga volvería a bajar todo.
   */
  private resolveAttachmentUrls(itemId: string, attachments: WorkItemAttachment[]): void {
    for (const a of attachments) {
      if (this.attachmentUrls[a.url]) continue
      this.worklog.getAttachmentBlob(itemId, a._id).subscribe((blob) => {
        if (blob) this.attachmentUrls = { ...this.attachmentUrls, [a.url]: URL.createObjectURL(blob) }
      })
    }
  }

  /** Los que se pintan: sin los retirados. */
  get visibleAttachments(): WorkItemAttachment[] {
    return this.attachments.filter((a) => !a.deletedAt)
  }

  /** Subidos a propósito como archivo, sean del tipo que sean. */
  get fileAttachments(): WorkItemAttachment[] {
    return this.visibleAttachments.filter((a) => !a.inline)
  }

  /** Pegados dentro de un texto (descripción o comentario). */
  get imageAttachments(): WorkItemAttachment[] {
    return this.visibleAttachments.filter((a) => a.inline)
  }

  /** ¿El evento toca un campo de texto libre? Entonces se anuncia sin volcar su contenido. */
  isLongText(h?: WorkItemHistory | null): boolean {
    return h?.kind === 'field' && LONG_TEXT_FIELDS.includes(h?.field ?? '')
  }

  //* Los object URL viven mientras el documento: cerrar el modal no los libera. Sin esto, abrir
  //* veinte tareas con capturas deja los veinte blobs en memoria hasta recargar la pestaña.
  ngOnDestroy(): void {
    for (const url of Object.values(this.attachmentUrls)) URL.revokeObjectURL(url)
    this.attachmentUrls = {}
  }

  openImage(url: string): void {
    this.lightboxUrl = url
    this.lightboxVisible = true
  }

  /**
   * Atajo de pegado que se le muestra al usuario, según su sistema. El pegado funciona igual en los
   * dos —es el mismo evento `paste`—, pero decirle "Ctrl+V" a alguien en Mac lo manda a probar una
   * tecla que ahí no hace nada.
   */
  get pasteKey(): string {
    const mac = /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent || '')
    return mac ? '⌘V' : 'Ctrl+V'
  }

  //* `key: 'worklog'` para que el mensaje salga en el <p-toast> del propio módulo (que vive
  //* dentro del árbol del diálogo) y no se pierda detrás de la máscara del modal.
  private toast(severity: 'success' | 'error', summary: string, detail: string): void {
    this.messageService.add({ key: 'worklog', severity, summary, detail, life: 5000 })
  }
}
